import React, { useState } from 'react';
import { useLang } from '@shootout-comps/i18n';
import { useAuth } from '@shootout-comps/core';

interface EmailVerificationPageProps {
  // Pending after signup — show "check email" screen
  email?: string;
  // Server redirected back with ?verify=success
  verifySuccess?: boolean;
  username?: string;
  // Server redirected back with ?verify=error
  verifyError?: string;
  // Callbacks
  onVerified: () => void;
  onSignIn: () => void;
}

export default function EmailVerificationPage({
  email,
  verifySuccess,
  username,
  verifyError,
  onVerified,
  onSignIn,
}: EmailVerificationPageProps) {
  const { resendVerification } = useAuth();
  const [resendEmail, setResendEmail] = useState(email ?? '');
  const { t } = useLang();
  const [resending, setResending]     = useState(false);
  const [resent, setResent]           = useState(false);
  const [resendError, setResendError] = useState('');

  const handleResend = async () => {
    const addr = resendEmail.trim();
    if (!addr) return;
    setResending(true); setResent(false); setResendError('');
    try {
      await resendVerification(addr);
      setResent(true);
    } catch (e) {
      setResendError(e instanceof Error ? e.message : 'Failed to resend');
    } finally {
      setResending(false);
    }
  };

  const Box = ({ children }: { children: React.ReactNode }) => (
    <div style={{ minHeight: '100dvh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--baize-dark)', padding: '1rem' }}>
      <div style={{ width: '100%', maxWidth: '440px', background: 'linear-gradient(135deg, var(--baize), var(--felt))', border: '2px solid rgba(201,168,76,0.35)', borderRadius: '14px', padding: 'clamp(1.75rem,6vw,2.5rem)', textAlign: 'center', boxShadow: '0 20px 60px rgba(0,0,0,0.5)' }}>
        {children}
      </div>
    </div>
  );

  // ── Success ──────────────────────────────────────────────────────────────────
  if (verifySuccess) return (
    <Box>
      <div style={{ fontSize: '3rem', marginBottom: '0.75rem' }}>✅</div>
      <h2 style={{ fontFamily: 'Bebas Neue', fontSize: '2rem', color: 'var(--gold)', marginBottom: '0.5rem' }}>
        Email Verified!
      </h2>
      <p style={{ color: 'var(--chalk-dim)', fontSize: '0.88rem', marginBottom: '0.5rem', lineHeight: 1.6 }}>
        {username ? `Welcome, ${username}! Your account is now active.` : 'Your account is now active.'}
      </p>
      <p style={{ color: 'var(--chalk-dim)', fontSize: '0.82rem', marginBottom: '1.75rem' }}>
        You can now sign in and start running your tournaments.
      </p>
      <button onClick={onVerified} style={goldBtnStyle}>
        {t.signInNow}
      </button>
    </Box>
  );

  // ── Error ────────────────────────────────────────────────────────────────────
  if (verifyError !== undefined) {
    const friendlyError = verifyError === 'expired'
      ? 'This verification link has expired. Please request a new one below.'
      : verifyError === 'invalid_token'
        ? 'This verification link is invalid or has already been used.'
        : 'Verification failed. Please request a new link below.';

    return (
      <Box>
        <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>⚠️</div>
        <h2 style={{ fontFamily: 'Bebas Neue', fontSize: '1.8rem', color: '#e74c3c', marginBottom: '0.5rem' }}>
          Verification Failed
        </h2>
        <p style={{ color: 'var(--chalk-dim)', fontSize: '0.85rem', marginBottom: '1.25rem', lineHeight: 1.6 }}>
          {friendlyError}
        </p>

        <div style={{ textAlign: 'left', marginBottom: '1rem' }}>
          <label style={labelStyle}>Enter your email to get a new link</label>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <input
              type="email"
              value={resendEmail}
              onChange={e => setResendEmail(e.target.value)}
              placeholder="you@example.com"
              style={{ ...inputStyle, flex: 1 }}
            />
            <button
              onClick={handleResend}
              disabled={resending || !resendEmail.trim()}
              style={{ background: 'rgba(201,168,76,0.15)', color: 'var(--gold)', border: '1px solid rgba(201,168,76,0.35)', borderRadius: '8px', padding: '0 0.85rem', fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer', minHeight: '44px', whiteSpace: 'nowrap', opacity: resending || !resendEmail.trim() ? 0.5 : 1 }}
            >
              {resending ? t.resending : t.resend}
            </button>
          </div>
          {resent && <p style={{ color: '#2ecc71', fontSize: '0.78rem', marginTop: '0.4rem' }}>✓ New link sent — check your inbox and spam folder</p>}
          {resendError && <p style={{ color: '#e74c3c', fontSize: '0.78rem', marginTop: '0.4rem' }}>{resendError}</p>}
        </div>

        <button onClick={onSignIn} style={ghostBtnStyle}>{t.backToSignIn}</button>
      </Box>
    );
  }

  // ── Pending (shown after signup) ──────────────────────────────────────────────
  return (
    <Box>
      <div style={{ fontSize: '3rem', marginBottom: '0.75rem' }}>📧</div>
      <h2 style={{ fontFamily: 'Bebas Neue', fontSize: '2rem', color: 'var(--gold)', marginBottom: '0.5rem' }}>
        Check Your Email
      </h2>
      <p style={{ color: 'var(--chalk)', fontSize: '0.95rem', marginBottom: '0.4rem', fontWeight: 500 }}>
        We've sent a verification link to:
      </p>
      {resendEmail && (
        <p style={{ color: 'var(--gold)', fontWeight: 700, fontSize: '0.95rem', marginBottom: '1rem', wordBreak: 'break-all' }}>
          {resendEmail}
        </p>
      )}
      <p style={{ color: 'var(--chalk-dim)', fontSize: '0.82rem', marginBottom: '1rem', lineHeight: 1.6 }}>
        Click the link in the email to activate your account. It expires in{' '}
        <strong style={{ color: 'var(--chalk)' }}>24 hours</strong>.
        Check your <strong style={{ color: 'var(--chalk)' }}>spam folder</strong> if you don't see it.
      </p>

      {/* Dev-mode hint */}
      {window.location.hostname === 'localhost' && (
        <div style={{ background: 'rgba(52,152,219,0.12)', border: '1px solid rgba(52,152,219,0.3)', borderRadius: '8px', padding: '0.75rem', marginBottom: '1.25rem', textAlign: 'left' }}>
          <p style={{ color: '#3498db', fontSize: '0.75rem', fontWeight: 700, marginBottom: '0.3rem' }}>🛠 Local development</p>
          <p style={{ color: 'var(--chalk-dim)', fontSize: '0.75rem', lineHeight: 1.5, margin: 0 }}>
            Check the <strong style={{ color: 'var(--chalk)' }}>API server console</strong> for an Ethereal preview URL to view the email and click the verification link.
          </p>
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
        <button onClick={handleResend} disabled={resending || resent}
          style={{ ...ghostBtnStyle, opacity: resent ? 0.6 : 1 }}>
          {resent ? t.newLinkSent : resending ? t.resending : t.didntGetIt}
        </button>
        <button onClick={onSignIn} style={ghostBtnStyle}>{t.backToSignIn}</button>
      </div>

      {resendError && <p style={{ color: '#e74c3c', fontSize: '0.78rem', marginTop: '0.75rem' }}>{resendError}</p>}
    </Box>
  );
}

const labelStyle: React.CSSProperties = { fontSize: '0.72rem', color: 'var(--chalk-dim)', textTransform: 'uppercase', letterSpacing: '0.1em', display: 'block', marginBottom: '0.35rem' };
const inputStyle: React.CSSProperties = { padding: '0.65rem 0.85rem', background: 'rgba(0,0,0,0.3)', border: '1.5px solid rgba(201,168,76,0.3)', borderRadius: '8px', color: 'var(--chalk)', outline: 'none', fontSize: '0.88rem', width: '100%', boxSizing: 'border-box' };
const goldBtnStyle: React.CSSProperties = { background: 'linear-gradient(135deg, var(--gold), var(--gold-light))', color: 'var(--baize-dark)', border: 'none', borderRadius: '8px', padding: '0.9rem 2rem', fontSize: '0.95rem', fontWeight: 700, cursor: 'pointer', width: '100%', minHeight: '48px' };
const ghostBtnStyle: React.CSSProperties = { background: 'transparent', color: 'var(--chalk-dim)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '8px', padding: '0.65rem', fontSize: '0.82rem', cursor: 'pointer', width: '100%', minHeight: '44px' };
